import React, { useMemo, useState } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area } from 'recharts';
import { Box, Typography } from '@mui/material';

import { Button } from 'framework/ui/mui-builder';
import { CustomizedYAxisLabel, CustomTooltip, CustomLegend } from 'domain/shared/core/chart';
import { REPORTED_TIME_CHARTS_COLORS } from '../useReportedHoursService';
import { useShowContext } from 'framework/show';
import { ProjectChartDataType } from '../types';

const HEIGHT_CHART = 170;

const ReportTimeByAssugmentCharts: React.FC = () => {
  const { data } = useShowContext<{ projectChartData: ProjectChartDataType[] }>();
  const { projectChartData: projectsData = [] } = data.getData();
  const [showAll, setShowAll] = useState(false);

  const shouldCollapse = projectsData.length > 2;

  const collapsedMaxHeight = useMemo(() => HEIGHT_CHART * 2, []); // Height of 2 Charts (px)
  const expandedMaxHeight = useMemo(() => projectsData.length * HEIGHT_CHART, [projectsData]); // Height of all Charts (px)

  return (
    <Box>
      <Typography display="block" textAlign="center" mb="10px" variant="body1">
        Reported Time vs Assignment by Project
      </Typography>

      <Box
        sx={{
          overflow: 'hidden',
          transition: 'max-height 0.4s ease',
          maxHeight: shouldCollapse && !showAll ? `${collapsedMaxHeight}px` : `${expandedMaxHeight}px`,
        }}
      >
        {projectsData.map((project: any, idx: number) => {
          const color = REPORTED_TIME_CHARTS_COLORS[idx % REPORTED_TIME_CHARTS_COLORS.length];
          const isPartiallyVisible = shouldCollapse && !showAll && idx === 1;

          return (
            <Box
              key={project.name}
              height="160px"
              mb="10px"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s ease',
                ...(isPartiallyVisible && {
                  WebkitMaskImage:
                    'linear-gradient(to top, var(--mui-palette-common-transparentWhite) 40%, var(--mui-palette-common-loadingOverlay) 70%)',
                  maskImage:
                    'linear-gradient(to top, var(--mui-palette-common-transparentWhite) 40%, var(--mui-palette-common-loadingOverlay) 70%)',
                  WebkitMaskSize: '100% 100%',
                  WebkitMaskRepeat: 'no-repeat',
                }),
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={project.data}>
                  <CartesianGrid strokeDasharray="2 2" />
                  <XAxis tick={{ fontSize: 12 }} dataKey="name" padding={{ left: 30, right: 30 }} />
                  <YAxis tick={{ fontSize: 12 }} label={<CustomizedYAxisLabel label={project.name} color={color} />} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} content={<CustomTooltip />} />
                  {idx === projectsData.length - 1 && (
                    <Legend wrapperStyle={{ fontSize: 12 }} content={<CustomLegend />} />
                  )}
                  <Area
                    type="stepAfter"
                    dataKey="rangeMin"
                    connectNulls={false}
                    stackId="range"
                    stroke="none"
                    fill="none"
                    dot={{ r: 2, stroke: 'var(--mui-palette-border-medium)', fill: 'var(--mui-palette-border-medium)' }}
                  />
                  <Area
                    type="stepAfter"
                    dataKey="rangeDelta"
                    connectNulls={false}
                    stackId="range"
                    stroke="none"
                    fill="var(--mui-palette-border-medium)"
                    dot={{ r: 2, stroke: 'var(--mui-palette-border-medium)', fill: 'var(--mui-palette-border-medium)' }}
                  />
                  <Line
                    type="linear"
                    dataKey="reportedTime"
                    stroke={color}
                    strokeWidth={2}
                    dot={{ r: 2, stroke: color, fill: color }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          );
        })}
      </Box>

      {shouldCollapse && (
        <Box textAlign="center" mt={1}>
          <Button sx={{ fontSize: '12px' }} link onClick={() => setShowAll(prev => !prev)}>
            {!showAll ? `Show all (${projectsData.length})` : 'Hide'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ReportTimeByAssugmentCharts;
